import type { PredictionMarketChoice, PredictionMarketSnapshot } from "@roman/shared/types/PredictionMarket";

export interface Choice {
  id: string;
  name: string;
  latestProbability?: number;
}

export interface ExtractedChoices {
  type: PredictionMarketType;
  choices: Choice[];
  sortedChoices: Choice[];
}

export const extractChoices = (predictionMarketHistory: PredictionMarketSnapshot[]): ExtractedChoices => {
  if (!predictionMarketHistory || predictionMarketHistory.length === 0) {
    return { type: PredictionMarketType.MULTIPLE, choices: [], sortedChoices: [] };
  }

  const firstSnapshot = predictionMarketHistory[0];
  if (!firstSnapshot?.predictionChoiceStates) {
    return { type: PredictionMarketType.MULTIPLE, choices: [], sortedChoices: [] };
  }

  // Get the latest snapshot for current probabilities
  const latestSnapshot = predictionMarketHistory[predictionMarketHistory.length - 1];
  const latestProbabilities = new Map<string, number>();

  if (latestSnapshot?.predictionChoiceStates) {
    for (const state of latestSnapshot.predictionChoiceStates) {
      latestProbabilities.set(state.choiceId, state.probability);
    }
  }

  const choices = firstSnapshot.predictionChoiceStates.map((state) => ({
    id: state.choiceId,
    name: state.choiceName,
    latestProbability: latestProbabilities.get(state.choiceId),
  }));

  // Sort choices by latestProbability in descending order if there are 3 or more
  const sortedChoices =
    choices.length >= 3
      ? [...choices].sort((a, b) => {
          const probA = a.latestProbability ?? 0;
          const probB = b.latestProbability ?? 0;
          return probB - probA;
        })
      : choices;

  const type = getPredictionMarketType(choices);

  return { type, choices, sortedChoices };
};

export enum PredictionMarketType {
  YESNO = "YESNO",
  MULTIPLE = "MULTIPLE",
}

export const getPredictionMarketType = (
  choices: Omit<PredictionMarketChoice, "probability" | "id">[],
): PredictionMarketType => {
  if (choices.length < 2) {
    throw new Error("There must be at least 2 choices");
  }
  if (choices.length === 2 && choices.some((c) => c.name === "Yes") && choices.some((c) => c.name === "No")) {
    return PredictionMarketType.YESNO;
  }
  return PredictionMarketType.MULTIPLE;
};

export const getYesNoChoices = <T extends PredictionMarketChoice>(
  choices: T[],
): {
  yesChoice: T;
  noChoice: T;
} => {
  const yesChoice = choices.find((c) => c.name === "Yes");
  const noChoice = choices.find((c) => c.name === "No");
  if (!yesChoice || !noChoice) {
    throw new Error("There must be Yes and No choices");
  }
  return { yesChoice, noChoice };
};

export const getYesNoLabel = (choice: PredictionMarketChoice): "YES" | "NO" => {
  if (choice.name === "Yes") return "YES";
  if (choice.name === "No") return "NO";
  throw new Error("Choice must be either 'Yes' or 'No'");
};
